interface IDay {
  name: string;
  dayOfWeek: number;
}

interface IStudioHours {
  [key: string]: {
    slots: Array<{ startTime: string; endTime: string }>;
    isWorkingDay: boolean;
  };
}

const ORDERED_DAYS: IDay[] = [
  { name: "Понеделник", dayOfWeek: 1 },
  { name: "Вторник", dayOfWeek: 2 },
  { name: "Сряда", dayOfWeek: 3 },
  { name: "Четвъртък", dayOfWeek: 4 },
  { name: "Петък", dayOfWeek: 5 },
  { name: "Събота", dayOfWeek: 6 },
  { name: "Неделя", dayOfWeek: 0 },
];

export const workingHoursUtility = {
  /**
   * Groups consecutive days together based on their day of week ordering
   * Handles Saturday to Sunday wrapping (dayOfWeek 6 to 0)
   */
  getConsecutiveGroups: (days: IDay[]): IDay[][] => {
    if (days.length === 0) return [];
    if (days.length === 1) return [days];

    const groups: IDay[][] = [];
    let currentGroup: IDay[] = [days[0]];

    for (let i = 1; i < days.length; i++) {
      const currentDay = days[i];
      const previousDay = days[i - 1];

      const isConsecutive =
        currentDay.dayOfWeek === previousDay.dayOfWeek + 1 ||
        (previousDay.dayOfWeek === 6 && currentDay.dayOfWeek === 0);

      if (isConsecutive) {
        currentGroup.push(currentDay);
      } else {
        groups.push([...currentGroup]);
        currentGroup = [currentDay];
      }
    }

    groups.push([...currentGroup]);

    return groups;
  },

  /**
   * Formats a group of days into a readable string
   * Single day: "Понеделник"
   * Two days: "Понеделник, Вторник"
   * More days: "Понеделник-Петък"
   */
  formatDaysGroup: (group: IDay[]): string => {
    if (group.length === 1) {
      return group[0].name;
    }
    if (group.length === 2) {
      return group.map((d) => d.name).join(", ");
    }
    return `${group[0].name}-${group[group.length - 1].name}`;
  },

  /**
   * Generates a formatted working hours text from studio hours data
   * Groups days with the same working hours together
   * Returns null if no valid data
   */
  formatWorkingHoursText: (studioHours: IStudioHours | null): string | null => {
    if (!studioHours) {
      return null;
    }

    const groupedDays: { [key: string]: IDay[] } = {};

    ORDERED_DAYS.forEach(({ name, dayOfWeek }) => {
      const dayHours = studioHours[dayOfWeek.toString()];

      if (dayHours && dayHours.isWorkingDay && dayHours.slots.length > 0) {
        const slotsText = dayHours.slots
          .map((slot) => `${slot.startTime}-${slot.endTime}`)
          .join("; ");

        if (!groupedDays[slotsText]) {
          groupedDays[slotsText] = [];
        }
        groupedDays[slotsText].push({ name, dayOfWeek });
      }
    });

    const workingHoursLines: string[] = [];

    Object.entries(groupedDays).forEach(([timeSlots, days]) => {
      const consecutiveGroups = workingHoursUtility.getConsecutiveGroups(days);

      consecutiveGroups.forEach((group) => {
        const daysText = workingHoursUtility.formatDaysGroup(group);
        workingHoursLines.push(`${daysText}: ${timeSlots}`);
      });
    });

    return workingHoursLines.length > 0 ? workingHoursLines.join("\n") : null;
  },
};
